/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useEffect, useState } from 'react'
import { View, BoxView, ActionButton } from '@components/atoms'
import { SeasonRegistrationModal } from '@components/molecules'
import { SeasonList } from '@components/organisms'

import { useAppDispatch, useAppSelector, seasonActions } from '@redux/index'
import {
	SeasonProductAddType,
	SeasonProductBatchUpdateType,
	SeasonProductDeleteType,
	SeasonProductType,
	SeasonProductUpdateType,
	SeasonType,
} from '@schemas'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { SeasonProductList } from '@components/organisms'
import { grey } from '@mui/material/colors'

const SeasonPage = () => {
	const dispatch = useAppDispatch()
	const { seasonList, selectedSeason, selectedYear, seasonProducts, loading, products } =
		useAppSelector(state => state.season)
	const [modalOpen, setModalOpen] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)
	const [currentSeasonProducts, setCurrentSeasonProducts] = useState<SeasonProductType[]>(
		[],
	)

	useEffect(() => {
		if (seasonProducts) {
			setCurrentSeasonProducts(JSON.parse(JSON.stringify(seasonProducts)))
		}
	}, [seasonProducts])

	const handleSeasonClick = (season: SeasonType) => {
		dispatch(seasonActions.SET_SELECTED_SEASON(season))
		dispatch(seasonActions.FETCH_SEASON_PRODUCT(season.id))
	}
	const handleYearChange = (newValue: Date | null) => {
		if (newValue) {
			dispatch(seasonActions.SET_SELECTED_YEAR(dayjs(newValue).format('YYYY')))
		}
	}

	const handleOpenModal = () => {
		setModalOpen(true)
	}

	const handleCloseModal = () => {
		setModalOpen(false)
	}

	const handleEditMode = () => {
		if (isEditMode) {
			setIsEditMode(false)
			setCurrentSeasonProducts(JSON.parse(JSON.stringify(seasonProducts)))
		} else {
			setIsEditMode(true)
		}
	}

	const handleUpdateSeasonProduct = (
		updatedProducts: SeasonProductBatchUpdateType[],
		deletedProducts: SeasonProductType[],
	) => {
		const batchUpdatePayload: SeasonProductBatchUpdateType[] = updatedProducts.map(
			product => {
				if (product.iudFlag === 'I') {
					return {
						iudFlag: 'I',
						seasonId: selectedSeason?.id,
						productId: product.productId,
						usd: product.usd,
						discountUsd: product.discountUsd,
						quantity: product.quantity,
						activeYn: product.activeYn,
					} as SeasonProductAddType
				} else if (product.iudFlag === 'U') {
					return {
						iudFlag: 'U',
						id: product.id,
						seasonId: selectedSeason?.id,
						productId: product.productId,
						usd: product.usd,
						quantity: product.quantity,
						discountUsd: product.discountUsd,
						activeYn: product.activeYn,
						partOrderedAt: product.partOrderedAt,
						productionStartAt: product.productionStartAt,
						deliveryStartAt: product.deliveryStartAt,
						deliveryCompletedAt: product.deliveryCompletedAt,
					} as SeasonProductUpdateType
				} else {
					return {
						iudFlag: 'D',
						id: product.id,
					} as SeasonProductDeleteType
				}
			},
		)

		dispatch(seasonActions.BATCH_UPDATE_SEASON_PRODUCT([...batchUpdatePayload]))
	}

	return (
		<View css={style}>
			<View className="seasonOuterView">
				<BoxView
					title="시즌 리스트"
					className="seasonView"
					rightHeaderAccessory={
						<ActionButton
							title="시즌 등록"
							iconName="plus"
							buttonColor="primary"
							variant="contained"
							onClick={handleOpenModal}
						/>
					}
				>
					<View className="seasonContentView">
						<View className="seasonViewContentHeader">
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									label="해당연도"
									value={dayjs(`${selectedYear as string}-01-01`).toDate()}
									onChange={handleYearChange}
									views={['year']}
									slotProps={{
										textField: {
											variant: 'outlined',
											size: 'small',
											style: {
												width: '8rem',
											},
										},
									}}
									format="yyyy-MM-dd"
									minDate={new Date('2025-01-01')}
								/>
							</LocalizationProvider>
						</View>
						<View className="listOuterView">
							{loading ? (
								<p>Loading...</p>
							) : (
								<SeasonList
									seasons={seasonList}
									selectedSeasonId={selectedSeason === null ? '' : selectedSeason.id}
									onSeasonClick={handleSeasonClick}
								/>
							)}
						</View>
					</View>
				</BoxView>
				<BoxView
					title={`시즌 상품 리스트 ${
						selectedSeason ? `- ${selectedSeason.title as string}` : ''
					}`}
					className="seasonProductView"
					rightHeaderAccessory={
						selectedSeason && (
							<ActionButton
								title={isEditMode ? '수정 종료' : '수정'}
								iconName={isEditMode ? 'cog-off' : 'cog'}
								buttonColor="primary"
								variant="contained"
								onClick={handleEditMode}
							/>
						)
					}
				>
					{selectedSeason ? (
						<SeasonProductList
							products={products}
							seasonProducts={currentSeasonProducts}
							isEditMode={isEditMode}
							onUpdateSeasonProduct={handleUpdateSeasonProduct}
						/>
					) : (
						<p>Select a season to view its products</p>
					)}
				</BoxView>
			</View>
			<SeasonRegistrationModal open={modalOpen} onClose={handleCloseModal} />
		</View>
	)
}

export default SeasonPage

const style = css({
	'.seasonOuterView': {
		position: 'relative',
		display: 'flex',

		'.seasonView': {
			width: '30rem',
			marginRight: '0.5rem',
			paddingLeft: '2rem',

			'.seasonContentView': {
				display: 'flex',
				flexDirection: 'column',
				gap: '1rem',
				width: '100%',
				height: '100%',

				'.seasonViewContentHeader': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					width: '100%',
					marginBottom: '-0.5rem',
					paddingTop: '0.5rem',
				},

				'.listOuterView': {
					width: '100%',
					height: '100%',
					paddingTop: '5px',
					paddingBottom: '5px',
					border: '1px solid',
					borderColor: grey[300],
					borderRadius: '5px',
					backgroundColor: grey[50],
					overflowY: 'auto',
				},
			},
		},
		'.seasonProductView': {
			width: 'calc(100% - 30rem)',
		},
	},
})
